import { AgFilterComponent } from '@ag-grid-community/angular';
import { IDoesFilterPassParams, IRowNode } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';

import { OrderNumber, OrderNumberFilter, FilterType } from '../../models/ag-grid';
import { FilterInstance } from '../../services/datacenter-filter';
import { DatacenterType } from '../../models/datacenter-aggrid-view';

import { DatacenterFilterParamsInterface } from './datacenter-date-filter.component';

@Component({
	selector: 'copo-datacenter-order-number-filter',
	templateUrl: './datacenter-order-number-filter.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule
	]
})
export class DatacenterOrderNumberFilterComponent implements AgFilterComponent, FilterInstance, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	referenceTextControl = new FormControl('', { nonNullable: true });
	itemTextControl = new FormControl('', { nonNullable: true });
	datacenterType: DatacenterType = 'order';
	datacenterTypeSub?: Subscription;
	private isClientSide = false;
	private model: OrderNumberFilter = { customFilterName: this.getCustomFilterName() };
	private params?: DatacenterFilterParamsInterface;
	private readonly itemSubscription: Subscription;
	private readonly referenceSubscription: Subscription;

	constructor(
		private readonly route: ActivatedRoute
	) {
		this.datacenterTypeSub = this.route.data.subscribe(res => {
			this.datacenterType = <DatacenterType> res['type'];
		});
		this.referenceSubscription = this.referenceTextControl.valueChanges.pipe(
			tap(() => {
				if (this.params) {
					this.params.api.setGridOption('loading', true);
				}
			}),
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.model.reference = value.trim();
			if (this.params) {
				this.params.api.setGridOption('loading', false);
				this.params.filterChangedCallback();
			}
		});
		this.itemSubscription = this.itemTextControl.valueChanges.pipe(
			tap(() => {
				if (this.params) {
					this.params.api.setGridOption('loading', true);
				}
			}),
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.model.item = value.trim();
			if (this.params) {
				this.params.api.setGridOption('loading', false);
				this.params.filterChangedCallback();
			}
		});
	}

	ngOnDestroy() {
		this.referenceSubscription.unsubscribe();
		this.itemSubscription.unsubscribe();
	}

	agInit(params: DatacenterFilterParamsInterface) {
		this.params = params;
		const rowModelType = params.rowModel.getType();
		this.isClientSide = rowModelType === 'clientSide';
	}

	isFilterActive() {
		return (this.model.reference !== undefined && this.model.reference.trim() !== '')
			|| (this.model.item !== undefined && this.model.item.trim() !== '')
			|| (this.model.list !== undefined && this.model.list.length > 0);
	}

	doesFilterPass(filterParams: IDoesFilterPassParams<Record<string, unknown>>) {
		if (!this.isClientSide) {
			return this.isFilterActive();
		}
		const value = this.getColumnValue(filterParams.node);
		if (!value) {
			return false;
		}
		if (
			this.model.list?.length && !this.model.list.some(orderNumber => this.matchOrderNumber(value, orderNumber))
		) {
			return false;
		}
		return this.matchOrderNumber(value, this.model);
	}

	getModel(): OrderNumberFilter | null {
		if (!this.isFilterActive()) {
			return null;
		}
		return { ...this.model, customFilterName: this.getCustomFilterName() };
	}

	setModel(model: OrderNumberFilter | null) {
		this.model = { ...model, customFilterName: this.getCustomFilterName() };
		this.referenceTextControl.setValue(model?.reference ?? '');
		this.itemTextControl.setValue(model?.item ?? '');
	}

	getCustomFilterName() {
		if (this.params?.colDef.filterParams && this.params.filterType
			&& this.params.filterType === 'DOCUMENT_ORDER_NUMBER_FILTER') {
			return FilterType.DOCUMENT_ORDER_NUMBER_FILTER;
		}
		return FilterType.ORDER_NUMBER_FILTER;
	}

	resetReference() {
		this.model.reference = undefined;
		this.referenceTextControl.reset();
	}

	resetItem() {
		this.model.item = undefined;
		this.itemTextControl.reset();
	}

	private getColumnValue(node: IRowNode<Record<string, unknown>>): string | undefined {
		if (!this.params || typeof node.data !== 'object') {
			return undefined;
		}
		const { data } = node;
		if (typeof this.params.colDef.valueGetter === 'function') {
			const value = <unknown> this.params.colDef.valueGetter({
				api: this.params.api,
				colDef: this.params.colDef,
				column: this.params.column,
				context: this.params.context,
				data,
				node,
				getValue(field: string) {
					return data[field];
				}
			});
			return typeof value === 'string' ? value : undefined;
		} else if (this.params.colDef.field) {
			const value = data[this.params.colDef.field];
			return typeof value === 'string' ? value : undefined;
		}
		return undefined;
	}

	private matchOrderNumber(value: string, orderNumber: OrderNumber) {
		/* more readable like that */
		/* eslint-disable sonarjs/prefer-single-boolean-return */
		if (orderNumber.reference && !value.includes(orderNumber.reference)) {
			return false;
		}
		if (orderNumber.item && !value.endsWith(`/${ orderNumber.item }`)) {
			return false;
		}
		return true;
		/* eslint-enable sonarjs/prefer-single-boolean-return */
	}
}
